// src/pages/CreditHistoryPage.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

export default function CreditHistoryPage() {
  const { authId, loading } = useBusinessGuard();
  const [creditBalance, setCreditBalance] = useState(0);
  const [events, setEvents] = useState([]);
  const [loadingCredit, setLoadingCredit] = useState(true);

  useEffect(() => {
    if (!authId) return;

    const loadCredit = async () => {
      setLoadingCredit(true);

      // 1) Get business credit_balance
      const { data: business, error: bizErr } = await supabase
        .from("businesses")
        .select("id, credit_balance")
        .eq("auth_id", authId)
        .single();

      if (bizErr || !business) {
        console.error("[CreditHistoryPage] business lookup error:", bizErr);
        setEvents([]);
        setLoadingCredit(false);
        return;
      }

      setCreditBalance(business.credit_balance || 0);

      // 2) Example: credit events from event_logs with type 'credit'
      const { data, error } = await supabase
        .from("event_logs")
        .select("id, type, payload, created_at")
        .eq("type", "credit")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) {
        console.error("[CreditHistoryPage] load error:", error);
        setEvents([]);
      } else {
        setEvents(data || []);
      }

      setLoadingCredit(false);
    };

    loadCredit();
  }, [authId]);

  if (loading) {
    return (
      <Box p={8}>
        <Text>Loading account…</Text>
      </Box>
    );
  }

  return (
    <Box p={8}>
      <Heading mb={4}>Credit history</Heading>
      <Text mb={6} color="gray.600">
        Your current credit balance and recent credit movements.
      </Text>

      {loadingCredit ? (
        <Text>Loading credit history…</Text>
      ) : (
        <>
          <Stat mb={6}>
            <StatLabel>Current credit balance</StatLabel>
            <StatNumber>£{Number(creditBalance).toFixed(2)}</StatNumber>
          </Stat>

          {events.length === 0 ? (
            <Text>No credit events recorded yet.</Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Event</Th>
                  <Th>Amount</Th>
                  <Th>Details</Th>
                  <Th>When</Th>
                </Tr>
              </Thead>
              <Tbody>
                {events.map((e) => {
                  const amount = e.payload?.amount ?? null;
                  const desc = e.payload?.description ?? "";
                  const type = e.payload?.type || "credit";
                  return (
                    <Tr key={e.id}>
                      <Td>{type}</Td>
                      <Td>
                        {amount != null ? `£${Number(amount).toFixed(2)}` : "—"}
                      </Td>
                      <Td>{desc}</Td>
                      <Td>
                        {e.created_at
                          ? new Date(e.created_at).toLocaleString("en-GB")
                          : "—"}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </>
      )}
    </Box>
  );
}
